// src/features/affiliate/OfferDetailRail.tsx
// Partner-facing offer detail — what does it pay, who can I target, and the
// entry point into "create a campaign for this offer".

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useAffiliateOffer, useI18n } from '@react/hooks';
import { Button, Badge, Alert } from '@react/components';
import type { Offer } from '@core/types/affiliate';
import { OfferStatusBadge, offerPayoutTypeLabel } from './badges';
import { errMessage, fmtDate, formatMoney } from './format';

interface OfferDetailRailProps {
  offerId: string;
  onClose: () => void;
  onCreateCampaign: (offer: Offer) => void;
}

export const OfferDetailRail = observer(({ offerId, onClose, onCreateCampaign }: OfferDetailRailProps) => {
  const { t, locale } = useI18n();
  const store = useAffiliateOffer();
  const [offer, setOffer] = useState<Offer | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setIsLoading(true);
    setError(null);
    store
      .GetOffer(offerId)
      .then(setOffer)
      .catch((e: unknown) => setError(errMessage(e, t('affiliate.offers.loadError'))))
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerId]);

  return (
    <aside className="w-full shrink-0 space-y-4 overflow-y-auto border-l border-border p-4 lg:w-96">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-foreground">{t('affiliate.offers.detailTitle')}</h2>
        <button onClick={onClose} aria-label={t('common.close')} className="rounded-md p-1 text-muted-foreground hover:bg-muted">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      {isLoading && <div className="h-40 animate-pulse rounded-md bg-muted" aria-busy="true" />}
      {error && <Alert variant="error">{error}</Alert>}

      {!isLoading && offer && (
        <div className="space-y-4">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold text-foreground">{offer.name}</h3>
              <OfferStatusBadge status={offer.status} />
            </div>
            {offer.description && <p className="mt-1 text-sm text-muted-foreground">{offer.description}</p>}
          </div>

          <div className="rounded-md border border-border p-3">
            <p className="text-xs uppercase text-muted-foreground">{t('affiliate.offers.payoutTerms')}</p>
            <p className="mt-1 text-sm text-foreground">
              {offerPayoutTypeLabel(t, offer.payout_type)}
              {' — '}
              {offer.payout_percent ? `${offer.payout_percent}%` : formatMoney(offer.payout_amount, locale)}
            </p>
            {offer.hold_period_days > 0 && (
              <p className="mt-1 text-xs text-muted-foreground">{t('affiliate.offers.holdPeriod', { days: offer.hold_period_days })}</p>
            )}
          </div>

          {(offer.allowed_countries.length > 0 || offer.blocked_countries.length > 0) && (
            <div>
              <p className="text-xs uppercase text-muted-foreground">{t('affiliate.offers.targeting')}</p>
              <div className="mt-1 flex flex-wrap gap-1">
                {offer.allowed_countries.map((c) => <Badge key={`a-${c}`} variant="success">{c}</Badge>)}
                {offer.blocked_countries.map((c) => <Badge key={`b-${c}`} variant="error">{c}</Badge>)}
              </div>
            </div>
          )}

          {offer.allowed_traffic_types.length > 0 && (
            <div>
              <p className="text-xs uppercase text-muted-foreground">{t('affiliate.offers.trafficTypes')}</p>
              <div className="mt-1 flex flex-wrap gap-1">
                {offer.allowed_traffic_types.map((tt) => <Badge key={tt} variant="default">{tt}</Badge>)}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2 text-sm">
            <div><p className="text-xs text-muted-foreground">{t('affiliate.offers.totalClicks')}</p><p className="text-foreground">{offer.total_clicks}</p></div>
            <div><p className="text-xs text-muted-foreground">{t('affiliate.offers.totalConversions')}</p><p className="text-foreground">{offer.total_conversions}</p></div>
          </div>

          {(offer.starts_at || offer.ends_at) && (
            <p className="text-xs text-muted-foreground">
              {offer.starts_at && t('affiliate.offers.startsAt', { date: fmtDate(offer.starts_at, locale) })}
              {offer.starts_at && offer.ends_at && ' · '}
              {offer.ends_at && t('affiliate.offers.endsAt', { date: fmtDate(offer.ends_at, locale) })}
            </p>
          )}

          {offer.preview_url && (
            <a href={offer.preview_url} target="_blank" rel="noreferrer" className="block text-sm text-primary hover:underline">
              {t('affiliate.offers.previewLink')}
            </a>
          )}

          <Button className="w-full" onClick={() => onCreateCampaign(offer)} disabled={offer.status !== 'OFFER_STATUS_ACTIVE'}>
            {t('affiliate.offers.createCampaign')}
          </Button>
        </div>
      )}
    </aside>
  );
});
